import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useParams, useNavigate,useSearchParams } from "react-router-dom";
import { DataContext } from "../../providers/DataProvider";

import ShowSucessMessage from "../modal/ShowSucessMessage";
import InputField from "../form_inputs/InputField";
import MarketingOptIn from "../form_inputs/MarketingOptIn";

import SubmitButton from "../form_inputs/SubmitButton";

import { renderFormField } from "../form_inputs/RenderFormField";

function DynamicEventSeriesForm({ formType }) {

  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirectTo");

  const [formState, setFormState] = useState({
    first_name: "",
    last_name: "",
    job_title: "",
    organisation: "",
    email: "",
    events: [],
    // eventSerieCopperTag: [],
    ...(formType === "event" ? { events: [] } : {}),
  });

  //Form Validation States
  const [errors, setErrors] = useState({
    first_name: "",
    last_name: "",
    job_title: "",
    organisation: "",
    email: "",
    events: "",
  });

  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formChanged, setFormChanged] = useState(false);
  const [triggerError, setTriggerError] = useState(false);
  const [marketingClicked, setMarketingClicked] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { eventseriesid } = useParams();

  const {
    eachEventSerieData,
    handlePopulateEachEventSeries,
    setListOfUserSubmissions,
  } = useContext(DataContext);

  useEffect(() => {
    console.log("EventSeriesId:", eventseriesid);
    handlePopulateEachEventSeries(eventseriesid);
    console.log("eachEventSerieDataInsideView:", eachEventSerieData);
  }, []);

  useEffect(() => {
    if (status === "success") {
      showSuccessMessage();
    }

    // Add a timeout to set loading to false after 3 seconds
    setTimeout(() => {
      if (status === "error") {
        setStatus("");
        setLoading(false);
        //setTriggerError(false);
      }
    }, 3000);
  }, [status]);

  //Validate form fields on change
  const validateField = (name, value) => {
    switch (name) {
      case "first_name":
        return !value || value.trim() === "" ? "First name is required" : "";
      case "last_name":
        return !value || value.trim() === "" ? "Last name is required" : "";
      case "job_title":
        return !value || value.trim() === "" ? "Job title is required" : "";
      case "organisation":
        return !value || value.trim() === "" ? "Organisation is required" : "";
      case "email":
        if (!value || value.trim() === "") return "Email is required";
        if (!value.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/))
          return "Please enter a valid email address";
        return "";
      case "events":
        return !value || value.length === 0
          ? "Please select at least one event"
          : "";
      default:
        return "";
    }
  };

  const handleEventSelection = (event) => {
    const { value, checked } = event.target;
    const updatedEvents = checked
      ? [...formState.events, value]
      : formState.events.filter((e) => e !== value);

    setFormState((prevState) => ({
      ...prevState,
      events: updatedEvents,
    }));

    // Clear error for events field if the form has been submitted
    if (formSubmitted) {
      setErrors((prev) => ({
        ...prev,
        events: "",
      }));
    }

    setFormChanged(true);
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    const inputValue = type === "checkbox" ? checked : value;

    setFormState((prevState) => ({
      ...prevState,
      [name]: inputValue,
    }));

    // Clear error for this field if the form has been submitted
    if (formSubmitted) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    setFormChanged(true);
  };

  const formValidation = () => {
    const newErrors = {
      first_name: validateField("first_name", formState.first_name),
      last_name: validateField("last_name", formState.last_name),
      job_title: validateField("job_title", formState.job_title),
      organisation: validateField("organisation", formState.organisation),
      email: validateField("email", formState.email),
      events: validateField("events", formState.events),
    };

    // Update the errors state
    setErrors(newErrors);

    // Return true if there are any errors
    return Object.values(newErrors).some((error) => error !== "");
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();

    //Validate the form before submission
    setFormSubmitted(true);
    const hasErrors = formValidation();

    if (hasErrors) {
      // Focus on the first field with an error
      const firstErrorField = Object.keys(errors).find(
        (key) => validateField(key, formState[key]) !== ""
      );
      if (firstErrorField) {
        document.getElementById(firstErrorField)?.focus();
      }
      return;
    }

    setLoading(true);
    setStatus("sending");
    console.log("Location:", location.pathname.split("/")[1]);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASEURL}/event_series/${eventseriesid}/submit`,
        {
          ...formState,
          signup_location: eachEventSerieData.title,
          event_serie_copper_tag: eachEventSerieData.copper_tag,
          show_on_slack: eachEventSerieData.formFields.filter(
            (field) => field.show_on_slack === true
          ),
        }
      );

      console.log("Response:", response.data);

      if (response.data) {
        console.log("Submission successful:", response.data);
        setLoading(false);

        setStatus("success");

        if (redirectTo) {
          setTimeout(() => {
            const parentWindow = window.parent;
            parentWindow.location.href = redirectTo;
          }, 1500);
        }
      }
    } catch (error) {
      console.error("Error:", error);
      setStatus("error");
      return;
    }

    setStatus("success");
  };

  const showSuccessMessage = () => {
    if (status === "success" && !showSuccess) {
      setShowSuccess(true);
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
        setShowSuccess(false);
        resetForm();
      }, 3000);
    }
  };

  const resetForm = () => {
    setFormState({
      first_name: "",
      last_name: "",
      job_title: "",
      organisation: "",
      email: "",
      events: [],
      ...(formType === "event" ? { events: [] } : {}),
    });
    setErrors({
      first_name: "",
      last_name: "",
      job_title: "",
      organisation: "",
      email: "",
      events: "",
    });
    setFormSubmitted(false);
    setFormChanged(false);
    setMarketingClicked(false);

    // Reset all checkboxes
    const checkboxes = document.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach((checkbox) => {
      checkbox.checked = false;
    });
  };

  // Whether form is disabled due to no events or loading
  const isFormDisabled =
    loading ||
    (eachEventSerieData.events || []).filter(
      (event) => event.show_on_form === true
    ).length === 0;

  return (
    <>
      <div className="flex justify-stretch">
        <div className="">
          {status === "success" && showSuccess && <ShowSucessMessage />}

          {status === "error" && (
            <div
              className="bg-red-100 text-red-700 p-4 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10 shadow-lg"
              role="alert"
            >
              <p className="font-bold">Error</p>
              <p>There was an error submitting your form. Please try again</p>
            </div>
          )}
          <div className="relative">
            {(eachEventSerieData.events || []).filter(
              (event) => event.show_on_form === true
            ).length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center z-10">
                <div className="text-center py-4 px-6 text-red-500 font-medium bg-yellow-100 rounded-md shadow-lg border border-yellow-300 max-w-md">
                  Signup events are currently closed for this event. Please
                  check our other events.
                </div>
              </div>
            )}
            <form
              className={`flex flex-col gap-3 w-full p-4 ${
                (eachEventSerieData.events || []).filter(
                  (event) => event.show_on_form === true
                ).length === 0
                  ? "bg-gray-300 opacity-75 cursor-not-allowed"
                  : ""
              } ${
                loading ? "opacity-50 border shadow cursor-not-allowed" : ""
              }`}
              onSubmit={handleFormSubmit}
              onChange={() => {
                setFormChanged(true);
              }}
            >
              <div className="flex justify-stretch gap-3">
                <div className="flex flex-col w-full">
                  <InputField
                    id="first_name"
                    label="First name"
                    value={formState.first_name || ""}
                    onChange={handleInputChange}
                    disabled={isFormDisabled}
                    required
                  />
                  {formSubmitted && errors.first_name && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.first_name}
                    </p>
                  )}
                </div>

                <div className="flex flex-col w-full">
                  <InputField
                    id="last_name"
                    label="Last Name"
                    value={formState.last_name || ""}
                    onChange={handleInputChange}
                    disabled={isFormDisabled}
                    required
                  />
                  {formSubmitted && errors.last_name && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.last_name}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex flex-col">
                <InputField
                  id="job_title"
                  label="Job Title"
                  value={formState.job_title || ""}
                  onChange={handleInputChange}
                  disabled={isFormDisabled}
                  required
                />
                {formSubmitted && errors.job_title && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.job_title}
                  </p>
                )}
              </div>

              <div className="flex flex-col">
                <InputField
                  id="organisation"
                  label="Organisation"
                  value={formState.organisation || ""}
                  onChange={handleInputChange}
                  disabled={isFormDisabled}
                  required
                />
                {formSubmitted && errors.organisation && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.organisation}
                  </p>
                )}
              </div>

              <div className="flex flex-col">
                <InputField
                  id="email"
                  label="Email"
                  type="email"
                  value={formState.email || ""}
                  onChange={handleInputChange}
                  disabled={isFormDisabled}
                  required
                />
                {formSubmitted && errors.email && (
                  <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                )}
              </div>

              {eachEventSerieData.formFields &&
                eachEventSerieData.formFields
                  .sort((a, b) => a.order - b.order)
                  .map((field) =>
                    renderFormField(
                      field,
                      formState,
                      handleInputChange,
                      loading,
                      eachEventSerieData
                    )
                  )}

              {eachEventSerieData.disclaimerText && (
                <p className="text-sm text-gray-600 mt-2 mb-4">
                  {eachEventSerieData.disclaimerText}
                </p>
              )}

              <div className="flex flex-col">
                <MarketingOptIn
                  id="accepts_marketing"
                  checked={formState.accepts_marketing || false}
                  onChange={(event) =>
                    setFormState({
                      ...formState,
                      accepts_marketing: event.target.checked,
                    })
                  }
                  disabled={isFormDisabled}
                />
              </div>

              <h4 className="text-2xl font-bold">I'm attending:</h4>
              <p>
                We'll add your selections below to the calendar associated with
                your email. If you've already signed up to one of the events
                below, you can leave it unchecked.
              </p>
              {formSubmitted && errors.events && (
                <p className="text-red-500 text-xs mt-1">{errors.events}</p>
              )}

              <div
                disabled={isFormDisabled}
                required
                className="grid grid-cols-1 gap-2 sm:grid-cols-2"
                onChange={handleEventSelection}
              >
                {(eachEventSerieData.events || [])
                  .filter((event) => event.show_on_form === true)
                  .sort((a, b) => {
                    const dateA = new Date(a.date);
                    const dateB = new Date(b.date);
                    return dateA - dateB;
                  })
                  .map((event) => (
                    <label
                      className={`${
                        loading ? "cursor-not-allowed" : ""
                      } checkbox-label text-md p-4`}
                      key={event.id}
                    >
                      <input
                        disabled={loading}
                        type="checkbox"
                        value={event.id}
                      />{" "}
                      {event.event_title}
                    </label>
                  ))}
              </div>

              <SubmitButton
                onClick={handleFormSubmit}
                disabled={
                  loading ||
                  !formChanged ||
                  (eachEventSerieData.events || []).filter(
                    (event) => event.show_on_form === true
                  ).length === 0
                }
                loading={loading}
              />
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default DynamicEventSeriesForm;
